import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
/**
 * The small subset of Markdown a chat answer actually uses, as React elements.
 *
 * Not a library, and never `dangerouslySetInnerHTML`. The text being rendered
 * has passed through a model that has just read a competitor's page title and a
 * customer's own datapoints, and the only way to be sure none of that becomes
 * markup is for none of it to be parsed as markup. Everything here produces
 * elements; a `<script>` in the text is a `<script>` on the screen, in prose.
 *
 * Supported, because these are what an answer here contains: paragraphs,
 * headings, blockquotes, separators, lists, fenced and inline code, bold, italic,
 * tables, and links to http(s).
 */

import type { ReactNode } from "react";

/** A name to pick out of the prose: the brand's own, or somebody else's. */
export interface Mark {
	text: string;
	you?: boolean;
}

export function Markdown({ text, marks }: { text: string; marks?: Mark[] }) {
	return <>{blocks(text, marks ?? [])}</>;
}

/**
 * The same text with the markup taken out, for a one-line excerpt. An excerpt
 * is cut mid-sentence, so parsing it would leave half a link or an unclosed
 * `**` on screen; what it needs is the words.
 */
export function plain(text: string): string {
	return (
		text
			.replace(/\[([^\]]+)\]\([^)\s]+\)/g, "$1")
			// A link the cut went through the middle of.
			.replace(/\[([^\]]+)\]\([^)]*$/, "$1")
			.replace(/\[([^\]]*)$/, "$1")
			.replace(/^\s{0,3}#{1,6}\s+/gm, "")
			.replace(/^\s*>\s?/gm, "")
			.replace(/\*\*|__|`/g, "")
			.replace(/(^|\s)\*(?=\S)|(?<=\S)\*(?=\s|$)/g, "$1")
			.replace(/\s+/g, " ")
			.trim()
	);
}

function blocks(text: string, marks: Mark[]): ReactNode[] {
	const lines = text.replace(/\r\n/g, "\n").split("\n");
	const out: ReactNode[] = [];
	let paragraph: string[] = [];
	let key = 0;

	const flush = () => {
		if (paragraph.length === 0) return;
		out.push(<p key={`p${key++}`}>{inline(paragraph.join(" "), marks)}</p>);
		paragraph = [];
	};

	for (let i = 0; i < lines.length; i++) {
		const line = lines[i];

		if (/^\s*>/.test(line)) {
			flush();
			const quoted: string[] = [];
			while (i < lines.length && /^\s*>/.test(lines[i])) quoted.push(lines[i++].replace(/^\s*> ?/, ""));
			i--;
			out.push(<blockquote key={`q${key++}`}>{blocks(quoted.join("\n"), marks)}</blockquote>);
			continue;
		}
		if (/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
			flush();
			out.push(<hr key={`hr${key++}`} />);
			continue;
		}

		if (line.startsWith("```")) {
			flush();
			const code: string[] = [];
			i++;
			while (i < lines.length && !lines[i].startsWith("```")) code.push(lines[i++]);
			out.push(
				<pre key={`c${key++}`}>
					<code>{code.join("\n")}</code>
				</pre>,
			);
			continue;
		}

		if (
			line.includes("|") &&
			i + 1 < lines.length &&
			tableCells(lines[i + 1]).every((cell) => /^:?-{3,}:?$/.test(cell))
		) {
			flush();
			const headers = tableCells(line);
			const rows: string[][] = [];
			i += 2;
			while (i < lines.length && lines[i].trim() && lines[i].includes("|")) rows.push(tableCells(lines[i++]));
			i--;
			out.push(
				<Table variant="resource" key={`t${key++}`}>
					<TableHeader>
						<TableRow>
							{headers.map((cell) => (
								<TableHead key={`th${key++}`}>{inline(cell, marks)}</TableHead>
							))}
						</TableRow>
					</TableHeader>
					<TableBody>
						{rows.map((row) => (
							<TableRow key={`tr${key++}`}>
								{headers.map((_, col) => (
									<TableCell key={`td${key++}`}>{inline(row[col] || "", marks)}</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>,
			);
			continue;
		}

		const heading = /^(#{1,4})\s+(.*)$/.exec(line);
		if (heading) {
			flush();
			// One level down: an answer in a side panel sits under the panel's
			// own heading, so its "#" is an h3 on the page.
			const Tag = (["h3", "h4", "h5", "h6"] as const)[heading[1].length - 1];
			out.push(<Tag key={`h${key++}`}>{inline(heading[2], marks)}</Tag>);
			continue;
		}

		if (/^\s*([-*+]|\d+\.)\s+/.test(line)) {
			flush();
			const ordered = /^\s*\d+\./.test(line);
			const items: string[] = [];
			while (i < lines.length && /^\s*([-*+]|\d+\.)\s+/.test(lines[i])) {
				items.push(lines[i].replace(/^\s*([-*+]|\d+\.)\s+/, ""));
				i++;
			}
			i--;
			// The list is derived from the text on every render, so its items have
			// no identity beyond their position and nothing to preserve across one.
			const children = items.map((item, n) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: positional by construction
				<li key={n}>{inline(item, marks)}</li>
			));
			out.push(ordered ? <ol key={`l${key++}`}>{children}</ol> : <ul key={`l${key++}`}>{children}</ul>);
			continue;
		}

		if (line.trim() === "") {
			flush();
			continue;
		}
		paragraph.push(line.trim());
	}
	flush();
	return out;
}

/** `**bold**`, `*italic*`, `` `code` `` and `[text](url)`, in one pass. */
function inline(text: string, marks: Mark[]): ReactNode[] {
	const pattern = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*\n]+\*)|(\[[^\]]+\]\([^)\s]+\))/g;
	const out: ReactNode[] = [];
	let last = 0;
	let key = 0;

	for (const match of text.matchAll(pattern)) {
		const at = match.index ?? 0;
		if (at > last) out.push(...highlight(text.slice(last, at), marks));
		const token = match[0];

		if (token.startsWith("`")) {
			out.push(<code key={key++}>{token.slice(1, -1)}</code>);
		} else if (token.startsWith("**")) {
			out.push(<strong key={key++}>{highlight(token.slice(2, -2), marks)}</strong>);
		} else if (token.startsWith("*")) {
			out.push(<em key={key++}>{token.slice(1, -1)}</em>);
		} else {
			const split = token.indexOf("](");
			const label = token.slice(1, split);
			const href = token.slice(split + 2, -1);
			// Anything that is not plainly http(s) is shown as the text it is.
			// A `javascript:` href in a model's answer is not a link this panel
			// is going to render.
			out.push(
				/^https?:\/\//i.test(href) ? (
					<a key={key++} href={href} target="_blank" rel="noopener noreferrer">
						{label}
					</a>
				) : (
					`${label} (${href})`
				),
			);
		}
		last = at + token.length;
	}

	if (last < text.length) out.push(...highlight(text.slice(last), marks));
	return out;
}

/**
 * Wrap every occurrence of a name in `<mark>`. Whole words for Latin names, so
 * "Asale" does not light up inside "Wholesale"; any occurrence for CJK, which
 * has no word breaks. The longest name first, so "OpenAI Platform" wins over
 * "OpenAI".
 */
function highlight(text: string, marks: Mark[]): ReactNode[] {
	const names = marks.filter((m) => m.text.trim().length > 1);
	if (names.length === 0 || !text) return [text];
	const quote = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	const sorted = [...names].sort((a, b) => b.text.length - a.text.length);
	const pattern = new RegExp(
		sorted
			.map((m) => {
				const e = quote(m.text.trim());
				return /^[ -~]+$/.test(m.text.trim()) ? `(?<![A-Za-z0-9])${e}(?![A-Za-z0-9])` : e;
			})
			.join("|"),
		"gi",
	);
	const out: ReactNode[] = [];
	let last = 0;
	for (const match of text.matchAll(pattern)) {
		const at = match.index ?? 0;
		if (at > last) out.push(text.slice(last, at));
		const you = sorted.find((m) => m.text.trim().toLowerCase() === match[0].toLowerCase())?.you;
		out.push(
			<mark key={at} className={you ? "mark-you" : undefined}>
				{match[0]}
			</mark>,
		);
		last = at + match[0].length;
	}
	if (last < text.length) out.push(text.slice(last));
	return out;
}

function tableCells(line: string): string[] {
	return line
		.trim()
		.replace(/^\|/, "")
		.replace(/\|$/, "")
		.split(/(?<!\\)\|/)
		.map((cell) => cell.trim().replace(/\\\|/g, "|"));
}
